import { ApplicationRef, ComponentFactoryResolver, ComponentRef, Injectable, Injector } from '@angular/core';
import { Formatter } from '@aptitude/shared-vanilla';
import { Identifier } from '../../../types/types';

interface ProductCategory extends Identifier {
  parents: Array<Identifier>;
  categoryCode?: string;
}

@Injectable({
  providedIn: 'root',
})

/**
 * @description These templates are quickly turning into a clustered mess. In some cases, they could easily be components.
 */
export class GridTemplateService {
  // Use arrow syntax here to stop Highcharts from mangling 'this'
  // With Ag-Grid you cannot specify both valueFormatter and cellRenderer so formatting must be done here

  // use by default
  generalFormatterTemplate = (value: unknown) => {
    return `${value}`;
  };

  twoLineWithTitle = (title: string, content: string) => {
    return `
      <div class="fw-bold mb-1 text-truncate custom-line-height-cell">
          ${title}
      </div>
      <div class="text-muted fw-normal text-truncate custom-line-height-cell">
        ${content}
      </div>
    `;
  };

  /* @deprecated use ThreeLinesWithLinkCellRendererComponent instead */
  threeLineWithTitleAsLink = (
    title: string,
    content = '',
    titleUrl?: string,
    status?: string,
    isAptitudeProduct = false,
  ) => {
    const showDiscontinueBadge =
      status && status === 'Discontinued' ? true : false;

    return `
      <div class="fw-bold mb-1 text-truncate custom-line-height-cell custom-two-line-cell">
        <span class="fw-bold mb-1">

        ${
          isAptitudeProduct
            ? '<i class="apt-logo-icon" data-test-selector="grid-aptitude-icon"></i>'
            : ''
        }

        ${
          showDiscontinueBadge
            ? '<span data-test-selector="grid-discontinued-badge" class="badge badge-pill bg-secondary">D/C</span>'
            : ''
        }
        <a href="${titleUrl}" target="_blank" data-test-selector="three-line-with-title-as-link-anchor"> ${title} </a>
        </span>
        <span data-test-selector="three-line-with-title-as-link-description" class="text-muted fw-normal">
          ${content}
        </span>
      </div>
    `;
  };

  leftAndRight = (left: string, right: string) => {
    return `
      <div class="left-and-right">
        <div class="left text-truncate">${left}</div>
        <div class="right">${right}</div>
      </div>`;
  };

  benchmarkCell = (benchmarkValue: number, coveragePercent: number) => {
    return `
    <div class="text-end custom-line-height-cell">
      <div class="fw-bold mb-1 text-info text-truncate">
        ${Formatter.currency(benchmarkValue)}
      </div>
      <div class="text-muted text-small text-truncate">
        ${Formatter.percent(coveragePercent)} Benchmarked
      </div>
    </div>
    `;
  };

  downloadIcon = (href: string) => {
    return `
      <div class="download-icon"><a href="${href}" target="_blank"><i class="fas fa-lg fa-download"></i></a></div>
    `;
  };

  public buildCategories = (category?: ProductCategory): Identifier[] => {
    if (!category) return [];

    const { name, id } = category;
    return [
      ...category.parents,
      {
        name,
        id,
      },
    ];
  };

  sellerTemplate(manufacturer: string, seller: string) {
    const sellerName = `
      <div class="mb-1 text-truncate custom-line-height-cell" data-test-selector="seller-name">
        ${seller}
      </div>
    `;
    if (!manufacturer) {
      return sellerName;
    }

    const manufacturerName = seller !== manufacturer ? manufacturer : '';
    return `${sellerName}
            <div class="text-muted fw-normal text-truncate custom-line-height-cell" data-test-selector="manufacturer-name">
              ${manufacturerName}
            </div>`;
  }

  // add below property to columnDef to add icon in header
  // headerComponentParams: {
  //   template: this.setpotentialSavingsIcon(),
  // },
  addHeaderIcon(iconClass: string) {
    return `
            <div class="ag-cell-label-container" role="presentation">
                    <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
                    <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
                    <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                        <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order ag-hidden"></span>
                        <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon ag-hidden"></span>
                        <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon ag-hidden"></span>
                        <span data-ref="eSortMixed" class="ag-header-icon ag-sort-mixed-icon ag-hidden"></span>
                        <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon ag-hidden"></span>
                        <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                        <i class="fw-bold me-1 ${iconClass}"></i>
                        <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                    </div>
                </div>`;
  }

  addHeaderImage({
    source,
    width = '18px',
    height = '18px',
  }: {
    source?: string;
    width?: string;
    height?: string;
  }) {
    return `
            <div class="ag-cell-label-container" role="presentation">
                    <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
                    <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
                    <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                        <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order ag-hidden"></span>
                        <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon ag-hidden"></span>
                        <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon ag-hidden"></span>
                        <span data-ref="eSortMixed" class="ag-header-icon ag-sort-mixed-icon ag-hidden"></span>
                        <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon ag-hidden"></span>
                        <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                        ${
                          source
                            ? `<img src="${source}" width="${width}" height="${height}" class="ms-1" />`
                            : ''
                        }
                        <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                    </div>
                </div>`;
  }

  agGridColumnHeaderTemplateForTwoLineText() {
    return `<div class="ag-cell-label-container ag-column-header-for-two-line-text" role="presentation">
              <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
              <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
              <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
              </div>
            </div>`;
  }

  agGridColumnHeaderTemplateForTwoLineTextWithIcon(iconClass: string) {
    return `<div class="ag-cell-label-container ag-column-header-for-two-line-text" role="presentation">
              <span  data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
              <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
              <div  data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span  data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span  data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span  data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span  data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                <span  data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                <span  data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                 <i class="fw-bold me-1 ${iconClass}"></i>
              </div>
            </div>`;
  }
}
